import { useState } from "react";
import { useAuthQuery as useQuery } from '../../shared/hooks/useAuthQuery';
import { api } from "../../convex/_generated/api";
import { Id } from "../../convex/_generated/dataModel";
import { Button } from "../../catalyst-components/button";
import { useNavigate } from "react-router-dom";
import { AdminRoutes } from "../../shared/models";

export default function KnowledgeBaseAnalytics() {
  const navigate = useNavigate();
  const [selectedEntryId, setSelectedEntryId] = useState<Id<"knowledge_base"> | null>(null);

  const analytics = useQuery(api.knowledgeBaseUsage.getUsageAnalytics);
  const mostUsed = useQuery(api.knowledgeBaseUsage.getMostUsedEntries, { limit: 10 });
  const leastUsed = useQuery(api.knowledgeBaseUsage.getLeastUsedEntries, { limit: 10 });
  const recentlyUsed = useQuery(api.knowledgeBaseUsage.getRecentlyUsedEntries, { limit: 10 });
  
  // Always call hooks, but skip query when no entry is selected
  const entryStats = useQuery(
    api.knowledgeBaseUsage.getEntryUsageStats,
    selectedEntryId ? { entryId: selectedEntryId } : "skip"
  );
  const entryHistory = useQuery(
    api.knowledgeBaseUsage.getEntryUsageHistory,
    selectedEntryId ? { entryId: selectedEntryId, limit: 20 } : "skip"
  );

  if (!analytics) {
    return <div className="min-h-screen bg-background pt-24 p-6">Loading analytics...</div>;
  }

  return (
    <div className="min-h-screen bg-background pt-24 p-6 space-y-6">
      <div className="flex items-center gap-4 mb-6">
        <Button color="light" onClick={() => navigate(AdminRoutes.KnowledgeBase)}>
          ← Back to Knowledge Base
        </Button>
        <h1 className="text-3xl font-bold">Knowledge Base Analytics</h1>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="text-sm text-gray-500 mb-1">Total Entries</div>
          <div className="text-3xl font-bold">{analytics.totalEntries}</div>
          <div className="text-xs text-gray-500 mt-2">
            {analytics.activeEntries} active
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <div className="text-sm text-gray-500 mb-1">Used Entries</div>
          <div className="text-3xl font-bold text-green-600">{analytics.usedEntries}</div>
          <div className="text-xs text-gray-500 mt-2">
            {analytics.activeEntries > 0
              ? Math.round((analytics.usedEntries / analytics.activeEntries) * 100)
              : 0}% of active
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <div className="text-sm text-gray-500 mb-1">Unused Entries</div>
          <div className="text-3xl font-bold text-amber-600">{analytics.unusedEntries}</div>
          <div className="text-xs text-gray-500 mt-2">
            May need improvement
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <div className="text-sm text-gray-500 mb-1">Total Usage</div>
          <div className="text-3xl font-bold text-blue-600">{analytics.totalUsageCount}</div>
          <div className="text-xs text-gray-500 mt-2">
            {analytics.uniqueUsers} unique users
          </div>
        </div>
      </div>

      {/* Usage by Type */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h2 className="text-xl font-semibold mb-4">Usage by Type</h2>
        <div className="space-y-3">
          {analytics.usageByType.map(({ type, count }) => (
            <div key={type} className="flex items-center justify-between">
              <span className="text-sm font-medium">{type}</span>
              <div className="flex items-center gap-2">
                <div className="w-48 bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-blue-600 h-2 rounded-full"
                    style={{
                      width: `${Math.min(100, (count / analytics.totalUsageCount) * 100)}%`,
                    }}
                  />
                </div>
                <span className="text-sm text-gray-600 w-12 text-right">{count}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Usage Trend (Last 30 Days) */}
      {analytics.usageTrend.length > 0 && (
        <div className="bg-white p-6 rounded-lg shadow">
          <h2 className="text-xl font-semibold mb-4">Usage Trend (Last 30 Days)</h2>
          <div className="h-48 flex items-end gap-1">
            {analytics.usageTrend.map(({ date, count }) => {
              const maxCount = Math.max(...analytics.usageTrend.map(d => d.count));
              const height = (count / maxCount) * 100;
              return (
                <div key={date} className="flex-1 flex flex-col items-center group">
                  <div
                    className="w-full bg-blue-600 rounded-t group-hover:bg-blue-700 transition-colors"
                    style={{ height: `${height}%` }}
                    title={`${date}: ${count} uses`}
                  />
                  <span className="text-xs text-gray-500 mt-1 hidden group-hover:block">
                    {new Date(date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Top Lists */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Most Used */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h2 className="text-xl font-semibold mb-4">Most Used</h2>
          <div className="space-y-3">
            {mostUsed?.map((entry) => (
              <div
                key={entry._id}
                className="border-l-4 border-green-500 pl-3 py-2 hover:bg-gray-50 cursor-pointer"
                onClick={() => setSelectedEntryId(entry._id)}
              >
                <div className="text-sm font-medium">{entry.title}</div>
                <div className="text-xs text-gray-500 flex items-center gap-2">
                  <span>{entry.typeName}</span>
                  <span>•</span>
                  <span className="font-semibold">{entry.timesUsed || 0}x</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Least Used */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h2 className="text-xl font-semibold mb-4">Least Used (Active)</h2>
          <div className="space-y-3">
            {leastUsed?.map((entry) => (
              <div
                key={entry._id}
                className="border-l-4 border-amber-500 pl-3 py-2 hover:bg-gray-50 cursor-pointer"
                onClick={() => setSelectedEntryId(entry._id)}
              >
                <div className="text-sm font-medium">{entry.title}</div>
                <div className="text-xs text-gray-500 flex items-center gap-2">
                  <span>{entry.typeName}</span>
                  <span>•</span>
                  <span className="font-semibold">{entry.timesUsed || 0}x</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Recently Used */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h2 className="text-xl font-semibold mb-4">Recently Used</h2>
          <div className="space-y-3">
            {recentlyUsed?.map((entry) => (
              <div
                key={entry._id}
                className="border-l-4 border-blue-500 pl-3 py-2 hover:bg-gray-50 cursor-pointer"
                onClick={() => setSelectedEntryId(entry._id)}
              >
                <div className="text-sm font-medium">{entry.title}</div>
                <div className="text-xs text-gray-500 flex items-center gap-2">
                  <span>{entry.typeName}</span>
                  <span>•</span>
                  {entry.lastUsedAt && (
                    <span>{new Date(entry.lastUsedAt).toLocaleDateString()}</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Entry Detail Modal */}
      {selectedEntryId && entryStats && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b sticky top-0 bg-white">
              <div className="flex justify-between items-start">
                <h2 className="text-2xl font-bold">Entry Usage Details</h2>
                <Button color="light" onClick={() => setSelectedEntryId(null)}>
                  Close
                </Button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* Stats Grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-gray-50 p-4 rounded">
                  <div className="text-sm text-gray-500">Total Usage</div>
                  <div className="text-2xl font-bold">{entryStats.totalUsage}</div>
                </div>
                <div className="bg-gray-50 p-4 rounded">
                  <div className="text-sm text-gray-500">Unique Users</div>
                  <div className="text-2xl font-bold">{entryStats.uniqueUsers}</div>
                </div>
                <div className="bg-gray-50 p-4 rounded">
                  <div className="text-sm text-gray-500">Last 7 Days</div>
                  <div className="text-2xl font-bold">{entryStats.last7Days}</div>
                </div>
                <div className="bg-gray-50 p-4 rounded">
                  <div className="text-sm text-gray-500">Last 30 Days</div>
                  <div className="text-2xl font-bold">{entryStats.last30Days}</div>
                </div>
              </div>

              {/* Top Search Queries */}
              {entryStats.topSearchQueries.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold mb-3">Top Search Queries</h3>
                  <div className="space-y-2">
                    {entryStats.topSearchQueries.map(({ query, count }) => (
                      <div key={query} className="flex justify-between items-center bg-gray-50 p-3 rounded">
                        <span className="text-sm font-medium">"{query}"</span>
                        <span className="text-sm text-gray-600">{count}x</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Usage History */}
              {entryHistory && entryHistory.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold mb-3">Recent Usage History</h3>
                  <div className="space-y-2">
                    {entryHistory.map((usage) => (
                      <div key={usage._id} className="border-l-2 border-blue-500 pl-4 py-2">
                        <div className="text-sm font-medium">{usage.userName}</div>
                        <div className="text-xs text-gray-500">
                          {new Date(usage.timestamp).toLocaleString()}
                        </div>
                        {usage.searchQuery && (
                          <div className="text-xs text-gray-600 mt-1">
                            Query: "{usage.searchQuery}"
                          </div>
                        )}
                        {usage.messageContent && (
                          <div className="text-xs text-gray-600 mt-1 italic">
                            "{usage.messageContent}..."
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
